/**
 * Returns the localized name for a given locale with fallback chain:
 * requested locale -> English -> French -> first available value -> empty string
 */
export function getLocalizedName(
  name: { [key: string]: string } | undefined | null,
  locale: string
): string {
  if (!name) return ""
  return name[locale] || name["en"] || name["fr"] || Object.values(name)[0] || ""
}

type LocalizedMap = { [key: string]: string }

/**
 * Normalizes a stored multilingual value (a locale → text map, or a legacy
 * plain string) into the `{ en, fr }` shape the edit forms bind to.
 */
export function toBilingualForm(value: unknown): { en: string; fr: string } {
  if (typeof value === 'string') return { en: value, fr: '' }
  if (value && typeof value === 'object') {
    const map = value as LocalizedMap
    return { en: map.en || '', fr: map.fr || '' }
  }
  return { en: '', fr: '' }
}

/** Trims every entry and drops the blank ones; `{}` when nothing is left. */
export function compactLocalized(value: LocalizedMap): LocalizedMap {
  const out: LocalizedMap = {}
  for (const [lang, text] of Object.entries(value)) {
    const trimmed = (text ?? '').trim()
    if (trimmed) out[lang] = trimmed
  }
  return out
}

/**
 * The PATCH value of an optional multilingual field (e.g. `description`):
 * `undefined` when the draft matches what is stored (leave it unchanged), the
 * compacted map otherwise — `{}` when every language was emptied, which the
 * API reads as "clear it".
 */
export function localizedPatchValue(draft: LocalizedMap, stored: unknown): LocalizedMap | undefined {
  const next = compactLocalized(draft)
  const current = compactLocalized(toBilingualForm(stored))
  const same =
    Object.keys(next).length === Object.keys(current).length &&
    Object.entries(next).every(([lang, text]) => current[lang] === text)
  return same ? undefined : next
}
